import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { HiUsers, HiUserGroup, HiTrendingUp, HiChartBar, HiIdentification } from "react-icons/hi";
import { getUsers } from "../store/slice/users";
import { getAssociates } from "../store/slice/associates";
import { getFixers } from "../store/slice/fixers";

const StatCard = ({ title, count, icon: Icon, gradient, textColor, onClick, loading }) => (
  <div
    onClick={onClick}
    className={`relative overflow-hidden rounded-2xl p-6 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${gradient}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className={`text-sm font-medium uppercase tracking-wide ${textColor} opacity-80`}>
          {title}
        </p>
        <div className={`mt-2 text-5xl font-bold ${textColor}`}>
          {loading ? <Spin size="small" /> : count ?? 0}
        </div>
      </div>
      <div className={`rounded-xl p-3 ${textColor} bg-white bg-opacity-20`}>
        <Icon size={32} />
      </div>
    </div>
    <div className={`mt-4 text-xs font-medium ${textColor} opacity-60`}>
      Click to view details →
    </div>
    <div className="absolute -bottom-4 -right-4 opacity-10">
      <Icon size={100} />
    </div>
  </div>
);

function Home() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { list: userList, isLoading: usersLoading } = useSelector((s) => s.users);
  const { list: associateList, isLoading: associatesLoading } = useSelector((s) => s.associates);
  const { list: fixerList, isLoading: fixersLoading } = useSelector((s) => s.fixers);
  const { fullname } = useSelector((s) => s.auth);

  useEffect(() => {
    dispatch(getUsers());
    dispatch(getAssociates());
    dispatch(getFixers());
  }, [dispatch]);

  const stats = [
    {
      title: "Total Users",
      count: userList?.length,
      icon: HiUsers,
      gradient: "bg-gradient-to-br from-orange-400 to-orange-600",
      textColor: "text-white",
      loading: usersLoading,
      onClick: () => navigate("/users"),
    },
    {
      title: "Total Associates",
      count: associateList?.length,
      icon: HiUserGroup,
      gradient: "bg-gradient-to-br from-blue-500 to-blue-700",
      textColor: "text-white",
      loading: associatesLoading,
      onClick: () => navigate("/associates"),
    },
    {
      title: "Total Service Partners",
      count: fixerList?.length,
      icon: HiIdentification,
      gradient: "bg-gradient-to-br from-amber-400 to-amber-600",
      textColor: "text-white",
      loading: fixersLoading,
      onClick: () => navigate("/fixers"),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          Welcome back{fullname ? `, ${fullname}` : ""}!
        </h1>
        <p className="mt-1 text-gray-500">
          Here's what's happening on your platform today.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((s) => (
          <StatCard key={s.title} {...s} />
        ))}

        {/* Placeholder cards for future metrics */}
        <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg opacity-40 select-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium uppercase tracking-wide text-white opacity-80">
                Total Services
              </p>
              <div className="mt-2 text-5xl font-bold text-white">—</div>
            </div>
            <div className="rounded-xl p-3 text-white bg-white bg-opacity-20">
              <HiChartBar size={32} />
            </div>
          </div>
          <div className="mt-4 text-xs font-medium text-white opacity-60">Coming soon</div>
        </div>

        <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-purple-400 to-purple-600 shadow-lg opacity-40 select-none">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium uppercase tracking-wide text-white opacity-80">
                Revenue
              </p>
              <div className="mt-2 text-5xl font-bold text-white">—</div>
            </div>
            <div className="rounded-xl p-3 text-white bg-white bg-opacity-20">
              <HiTrendingUp size={32} />
            </div>
          </div>
          <div className="mt-4 text-xs font-medium text-white opacity-60">Coming soon</div>
        </div>
      </div>

      {/* Summary Table */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Platform Summary</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-600">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="pb-3 font-semibold text-gray-500 uppercase text-xs tracking-wide">
                  Category
                </th>
                <th className="pb-3 font-semibold text-gray-500 uppercase text-xs tracking-wide">
                  Count
                </th>
                <th className="pb-3 font-semibold text-gray-500 uppercase text-xs tracking-wide">
                  Status
                </th>
                <th className="pb-3 font-semibold text-gray-500 uppercase text-xs tracking-wide">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-orange-100 text-orange-500">
                    <HiUsers size={18} />
                  </span>
                  <span className="font-medium">Users</span>
                </td>
                <td className="py-4">
                  {usersLoading ? <Spin size="small" /> : (
                    <span className="font-bold text-gray-800">{userList?.length ?? 0}</span>
                  )}
                </td>
                <td className="py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    Active
                  </span>
                </td>
                <td className="py-4">
                  <button
                    onClick={() => navigate("/users")}
                    className="text-orange-500 hover:text-orange-700 font-medium text-sm transition-colors"
                  >
                    View all →
                  </button>
                </td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-500">
                    <HiUserGroup size={18} />
                  </span>
                  <span className="font-medium">Associates</span>
                </td>
                <td className="py-4">
                  {associatesLoading ? <Spin size="small" /> : (
                    <span className="font-bold text-gray-800">{associateList?.length ?? 0}</span>
                  )}
                </td>
                <td className="py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    Active
                  </span>
                </td>
                <td className="py-4">
                  <button
                    onClick={() => navigate("/associates")}
                    className="text-blue-500 hover:text-blue-700 font-medium text-sm transition-colors"
                  >
                    View all →
                  </button>
                </td>
              </tr>
              <tr className="hover:bg-gray-50 transition-colors">
                <td className="py-4 flex items-center gap-3">
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-500">
                    <HiIdentification size={18} />
                  </span>
                  <span className="font-medium">Service Partners</span>
                </td>
                <td className="py-4">
                  {fixersLoading ? <Spin size="small" /> : (
                    <span className="font-bold text-gray-800">{fixerList?.length ?? 0}</span>
                  )}
                </td>
                <td className="py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    Active
                  </span>
                </td>
                <td className="py-4">
                  <button
                    onClick={() => navigate("/fixers")}
                    className="text-amber-500 hover:text-amber-700 font-medium text-sm transition-colors"
                  >
                    View all →
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Home;
